import React, { useEffect, useState } from "react";
import {
  Badge,
  Breadcrumb,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
} from "react-bootstrap";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { useLocation, useNavigate } from "react-router-dom";
import fuziElevatorsApi from "../../api/fuziElevatorsApi";
import Confirm from "../Confirm";
import PubSub from "pubsub-js";
import { ROUTES } from "../../constants/webRouting";
import { formatDateForView, getUserDetails } from "../../utils/helper";
import RelatedListFiles from "../file/RelatedListFiles";
import FilesCreate from "../file/FilesCreate";
import PERMISSIONS from "../../constants/permissions";

const SiteVisitReportView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const userInfo = getUserDetails();

  const [report, setReport] = useState(location?.state ? location.state : {});
  const [modalShow, setModalShow] = useState(false);
  const [modalShowFile, setModalShowFile] = useState(false);
  const [refreshFileList, setRefreshFileList] = useState(false);
  const [relatedListFiles, setRelatedListFiles] = useState(true);

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    let id = report?.id;
    if (
      !id &&
      location.hasOwnProperty("pathname") &&
      location.pathname.split("/").length >= 3
    ) {
      id = location.pathname.split("/")[2];
    }
    if (!id) return;

    const result = await fuziElevatorsApi.fetchSiteVisitReportById(id);
    if (result) setReport(result);
  };

  const deleteReport = async () => {
    const result = await fuziElevatorsApi.deleteSiteVisitReportById(report.id);
    if (result?.success) {
      PubSub.publish("RECORD_SAVED_TOAST", {
        title: "Deleted",
        message: "Record deleted successfully",
      });
      navigate(ROUTES.SITE_VISIT_REPORT.LIST);
    } else {
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Error",
        message: result?.errors || "Some error found",
      });
    }
  };

  const handleSelect = (key) => setRelatedListFiles(key === "files");

  const submitFiles = () => {
    setModalShowFile(false);
    setRefreshFileList(Date.now());
  };

  // Helper: display comma-separated multi-value fields
  const displayMulti = (val) =>
    val
      ? val.split(",").map((v) => v.trim()).join(", ")
      : "-";

  // Compute total openings from line_items (same logic as edit page)
  const calcTotalOpenings = (items) => {
    if (!Array.isArray(items) || items.length === 0) return report.number_of_openings || "-";
    return items.reduce(
      (sum, s) => sum + (s.opening_side === "Both" ? 2 : 1),
      0
    );
  };


  // DB Column Mapping:
  //   floor_to   = floor NAME  (e.g. "Ground Floor")
  //   floor_from = opening SIDE ("Front"/"Rear"/"Both")
  const OPENING_VALUES = ["Front", "Rear", "Both"];

  const getFloorName = (item, i) => {
    if (OPENING_VALUES.includes(item.floor_from)) {
      return item.floor_to || "Floor " + (i + 1);
    }
    return item.floor_to || item.floor_from || "Floor " + (i + 1);
  };

  const getOpeningSide = (item) => {
    return OPENING_VALUES.includes(item.floor_from) ? item.floor_from : "Front";
  };

  return (
    <Container className="pb-5">
      {report && (
        <>
          {modalShow && (
            <Confirm
              show={modalShow}
              onHide={() => setModalShow(false)}
              deleteLead={deleteReport}
              title="Confirm delete?"
              message="You are going to delete the record. Are you sure?"
              table="site_visit_report"
            />
          )}

          <Row className="view-form mx-auto">
            {/* Breadcrumb */}
            <Col md={12}>
              <Breadcrumb>
                <Breadcrumb.Item onClick={() => navigate(ROUTES.HOME)}>
                  Home
                </Breadcrumb.Item>
                <Breadcrumb.Item onClick={() => navigate(ROUTES.SITE_VISIT_REPORT.LIST)}>
                  Site Visit Reports
                </Breadcrumb.Item>
              </Breadcrumb>
            </Col>

            <Col lg={12}>
              {/* ── Header ───────────────────────────── */}
              <Container className="pb-3 sticky-card-header-view">
                <Row className="view-form-header-lead bg-custom align-items-center rounded">
                  <Col lg={8}>
                    Site Visit Report
                    <h5>{report.customer_name || report.enquiry_no || ""}</h5>
                  </Col>
                  <Col lg={4} className="d-flex justify-content-end gap-3">
                    {[PERMISSIONS.MODIFY_ALL, PERMISSIONS?.MODIFY_SITE_VISIT].some((p) =>
                      userInfo?.permissions?.map((x) => x.name).includes(p)
                    ) && (
                      <Button
                        size="sm"
                        className="btn-edit"
                        onClick={() =>
                          navigate(`${ROUTES.SITE_VISIT_REPORT.LIST}/${report.id}/e`, {
                            state: report,
                          })
                        }
                      >
                        <i className="fa-regular fa-pen-to-square" />
                      </Button>
                    )}
                    {[PERMISSIONS.DELETE_ALL, PERMISSIONS?.DELETE_SITE_VISIT].some((p) =>
                      userInfo?.permissions?.map((x) => x.name).includes(p)
                    ) && (
                      <Button
                        size="sm"
                        className="btn-delete"
                        onClick={() => setModalShow(true)}
                      >
                        <i className="fa fa-trash-o" />
                      </Button>
                    )}
                  </Col>
                </Row>
              </Container>

              {/* ── Details Card ──────────────────────── */}
              <Card>
                <CardHeader className="sticky-tabs-header py-3">
                  <Col lg={12} style={{ color: "#fff" }}>
                    <Tabs
                      defaultActiveKey="details"
                      id="uncontrolled-tab-example"
                      style={{ marginBottom: "12px" }}
                    >
                      <Tab eventKey="details" title="Details" />
                    </Tabs>
                  </Col>
                </CardHeader>

                <CardBody className="card-body-scroll">
                  <Container>
                    <Row>

                      {/* ── Basic Information ─────────── */}
                      <Col md={12} className="section-header">Basic Information</Col>

                      <Col lg={6}>
                        <label>Enquiry No</label>
                        <span>{report.enquiry_no || " "}</span>
                      </Col>
                      <Col lg={6}>
                        <label>Customer Name</label>
                        <span>{report.customer_name || " "}</span>
                      </Col>
                      <Col lg={6}>
                        <label>Elevator Type</label>
                        <span>{report.elevator_type || " "}</span>
                      </Col>
                      <Col lg={6}>
                        <label>Building Type</label>
                        <span>{report.segment || " "}</span>
                      </Col>
                      <Col lg={6}>
                        <label>Site Address</label>
                        <span>{report.site_address || " "}</span>
                      </Col>
                      <Col lg={6}>
                        <label>Mobile No.</label>
                        <span>{report.phone || " "}</span>
                      </Col>
                      <Col lg={6}>
                        <label>Site Person Name</label>
                        <span>{report.site_person_name || " "}</span>
                      </Col>
                      <Col lg={6}>
                        <label>Site Person Mobile</label>
                        <span>{report.site_person_mobile || " "}</span>
                      </Col>
                      <Col lg={6}>
                        <label>Assigned Users</label>
                        <span>
                          <div className="d-flex flex-wrap gap-1">
                            {Array.isArray(report.owner_ids) && report.owner_ids.length > 0
                              ? report.owner_ids.map((item, index) => (
                                  <Badge bg="warning" key={index}>
                                    <span className="text-dark fw-bold">
                                      {item?.label || item}
                                    </span>
                                  </Badge>
                                ))
                              : "-"}
                          </div>
                        </span>
                      </Col>

                      {/* ── Technical Information ─────── */}
                      <Col md={12} className="section-header">Technical Information</Col>

                      <Col lg={6}>
                        <label>Pit Available (mm)</label>
                        <span>{report.lift_pit || " "}</span>
                      </Col>
                      <Col lg={6}>
                        <label>Machine Room Available</label>
                        <span>{report.machine_room || " "}</span>
                      </Col>
                      <Col lg={6}>
                        <label>Number of Stops</label>
                        <span>{report.total_stops || " "}</span>
                      </Col>
                      <Col lg={6}>
                        <label>Number of Openings</label>
                        <span>{calcTotalOpenings(report.line_items)}</span>
                      </Col>
                      <Col lg={6}>
                        <label>Motor Type</label>
                        <span>{displayMulti(report.motor)}</span>
                      </Col>

                      {/* ── Door & Cabin Information ───── */}
                      <Col md={12} className="section-header">Door & Cabin Information</Col>

                      <Col lg={6}>
                        <label>Cabin Finish</label>
                        <span>{displayMulti(report.cabin_metal)}</span>
                      </Col>
                      <Col lg={6}>
                        <label>Door Vision</label>
                        <span>{displayMulti(report.door_vision)}</span>
                      </Col>
                      <Col lg={6}>
                        <label>Door Opening</label>
                        <span>{report.door_opening || " "}</span>
                      </Col>
                      <Col lg={6}>
                        <label>Number of Door Panels</label>
                        <span>{report.door_panels || " "}</span>
                      </Col>
                      <Col lg={6}>
                        <label>Door Width (mm)</label>
                        <span>{report.door_width || " "}</span>
                      </Col>
                      <Col lg={6}>
                        <label>Door Height (mm)</label>
                        <span>{report.door_height || " "}</span>
                      </Col>

                      {/* ── Car Size & Capacity ───────── */}
                      <Col md={12} className="section-header">Car Size & Capacity</Col>

                      <Col lg={6}>
                        <label>Car Width (mm)</label>
                        <span>{report.car_internal_width || " "}</span>
                      </Col>
                      <Col lg={6}>
                        <label>Car Depth (mm)</label>
                        <span>{report.car_internal_depth || " "}</span>
                      </Col>
                      <Col lg={6}>
                        <label>Capacity (Persons)</label>
                        <span>{report.passenger_capacity || " "}</span>
                      </Col>
                      <Col lg={6}>
                        <label>Capacity (Kg)</label>
                        <span>{report.elevator_capacity || " "}</span>
                      </Col>

                      {/* ── Lift Well Size ────────────── */}
                      <Col md={12} className="section-header">Lift Well Size</Col>

                      <Col lg={6}>
                        <label>Lift Well Width (mm)</label>
                        <span>{report.shaft_width || " "}</span>
                      </Col>
                      <Col lg={6}>
                        <label>Lift Well Depth (mm)</label>
                        <span>{report.shaft_debth || " "}</span>
                      </Col>

                      {/* NEW: Overhead Height */}
                      <Col lg={6}>
                        <label>Overhead Height (mm)</label>
                        <span>{report.overhead_height || " "}</span>
                      </Col>

                      <Col lg={6}>
                        <label>Plaster</label>
                        <span>{report.shaft_construction || " "}</span>
                      </Col>

                      {/* ── Visit Information ─────────── */}
                      <Col md={12} className="section-header">Visit Information</Col>

                      <Col lg={6}>
                        <label>Site Visited By</label>
                        <span>{report.engineer_name || " "}</span>
                      </Col>
                      <Col lg={6}>
                        <label>Site Visit Date</label>
                        <span>{formatDateForView(report.site_visit_date || "")}</span>
                      </Col>
                      <Col lg={6}>
                        <label>Created Date</label>
                        <span>{formatDateForView(report.createddate || "")}</span>
                      </Col>
                      <Col lg={6}>
                        <label>Remark</label>
                        <span>{report.remark || " "}</span>
                      </Col>

                      {/* ── Floor Stops & Door Openings ── */}
                      {Array.isArray(report.line_items) && report.line_items.length > 0 && (
                        <>
                          <Col md={12} className="section-header">
                            Floor Stops & Door Openings
                          </Col>
                          <Col lg={12}>
                            <table className="table table-bordered table-sm table-hover">
                              <thead className="bg-custom text-white">
                                <tr>
                                  <th>#</th>
                                  <th>Floor Name</th>
                                  <th>Opening Side</th>
                                  <th>Height (mm)</th>
                                </tr>
                              </thead>
                              <tbody>
                                {report.line_items.map((item, i) => (
                                  <tr key={i}>
                                    <td>{i + 1}</td>
                                    <td>{getFloorName(item, i)}</td>
                                    <td>{getOpeningSide(item)}</td>
                                    <td>{item.height_mm || "-"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </Col>
                        </>
                      )}

                    </Row>
                  </Container>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* ── Related Lists Card ──────────────────── */}
          <Card bg="light" text="light" className="mb-2 mt-4 shadow">
            <Card.Header className="d-flex justify-content-between">
              <Tabs
                defaultActiveKey="files"
                id="svr-related-tabs"
                onSelect={handleSelect}
              >
                {[PERMISSIONS.VIEW_ALL, PERMISSIONS?.VIEW_FILE].some((p) =>
                  userInfo?.permissions?.map((x) => x.name).includes(p)
                ) && <Tab eventKey="files" title="Files" />}
              </Tabs>

              {relatedListFiles &&
                [PERMISSIONS.MODIFY_ALL, PERMISSIONS?.MODIFY_FILE].some((p) =>
                  userInfo?.permissions?.map((x) => x.name).includes(p)
                ) && (
                  <Button
                    size="sm"
                    className="btn-custom float-right"
                    onClick={() => setModalShowFile(true)}
                  >
                    New File
                  </Button>
                )}

              {modalShowFile && (
                <FilesCreate
                  show={modalShowFile}
                  onHide={() => setModalShowFile(false)}
                  parent={report}
                  table="site_visit_report"
                  submitFiles={submitFiles}
                />
              )}
            </Card.Header>

            <Card.Body>
              {relatedListFiles &&
                [PERMISSIONS.VIEW_ALL, PERMISSIONS?.VIEW_FILE].some((p) =>
                  userInfo?.permissions?.map((x) => x.name).includes(p)
                ) && (
                  <RelatedListFiles
                    refreshFileList={refreshFileList}
                    parent={report}
                    submitFiles={submitFiles}
                  />
                )}
            </Card.Body>
          </Card>
        </>
      )}
    </Container>
  );
};

export default SiteVisitReportView;